import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

/**
 * Catches rendering errors thrown by any public page (ServicePageView,
 * LegalPage, Home, etc.) and shows a graceful fallback instead of the
 * blank-white screen React falls back to by default.
 *
 * Why: we just had a bug where a duplicate seed pushed two `hero`
 * sections onto a page, React threw on the duplicate React-key, and the
 * whole public route went white with no feedback for the visitor. The
 * boundary makes the next class of bug visible — a "Something went wrong"
 * card with a back-to-home link — and logs the error to the browser
 * console so we can see it in DevTools.
 */
export default class PublicErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to DevTools so we can diagnose without redeploying.
    // eslint-disable-next-line no-console
    console.error('[PublicErrorBoundary] render failed:', error, info);
    this.setState({ info });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <section className="min-h-[70vh] flex items-center justify-center px-4 py-20 bg-slate-50">
        <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-2xl p-8 shadow-lg">
          <div className="w-14 h-14 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={26} />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Something went wrong.</h1>
          <p className="text-sm text-slate-600 mt-2">
            This page hit an unexpected error while rendering. The team has been notified.
          </p>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-shadow"
            >
              ← Back to home
            </Link>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold border border-slate-200 hover:bg-slate-200 transition"
            >
              Reload page
            </button>
          </div>
          {process.env.NODE_ENV !== 'production' && this.state.error?.message && (
            <pre className="mt-5 text-left text-[11px] bg-slate-900 text-rose-200 p-3 rounded-lg overflow-x-auto">
              {String(this.state.error.message)}
            </pre>
          )}
        </div>
      </section>
    );
  }
}
